import { useEffect, useState } from 'react';

/**
 * Per-tier display config (XT range shown to users on machine cards).
 * FRONTEND ONLY — actual mining reward comes from the contract's dailyRewardUSDT.
 *
 * Storage tiers (fetched in this order on first mount):
 *   1. Server-shared JSON at  /data/machine-display.json   (source of truth for all users)
 *   2. localStorage cache      xterra_machine_display_v1
 *   3. Hard-coded DEFAULTS below
 *
 * When an admin saves, we POST to /api/machine-display.php which writes
 * the JSON file atomically so every visitor sees the new values on their
 * next load. The admin UI is gated by wallet connection on the frontend.
 */
const STORAGE_KEY  = 'xterra_machine_display_v1';
const REMOTE_JSON  = '/data/machine-display.json';
const REMOTE_API   = '/api/machine-display.php';
const CHANGE_EVENT = 'xterra:machine-display-change';

// Defaults: [minXT, maxXT] per tier. Admin can override.
const DEFAULTS = [
  { minXt: '1',    maxXt: '2'    },  // N1
  { minXt: '5',    maxXt: '10'   },  // N2
  { minXt: '10',   maxXt: '20'   },  // N3
  { minXt: '25',   maxXt: '50'   },  // X1
  { minXt: '50',   maxXt: '100'  },  // X2
  { minXt: '100',  maxXt: '200'  },  // X3
  { minXt: '250',  maxXt: '500'  },  // Pro
  { minXt: '500',  maxXt: '1000' },  // Ultra
  { minXt: '1000', maxXt: '2000' },  // Titan
];

function normalize(arr) {
  if (!Array.isArray(arr) || arr.length !== 9) return DEFAULTS;
  return arr.map((p, i) => ({
    minXt: String(p?.minXt ?? DEFAULTS[i].minXt),
    maxXt: String(p?.maxXt ?? DEFAULTS[i].maxXt),
  }));
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return normalize(JSON.parse(raw));
  } catch {
    return DEFAULTS;
  }
}

// Fetch the shared JSON. If it fails (404, network, dev server), we just
// keep whatever we already have from localStorage/defaults.
async function fetchRemote() {
  try {
    const r = await fetch(REMOTE_JSON, { cache: 'no-store' });
    if (!r.ok) return null;
    const j = await r.json();
    return normalize(j);
  } catch {
    return null;
  }
}

export function useMachineDisplay() {
  const [config, setConfig] = useState(() => loadFromStorage());

  // On mount: pull the shared server copy so every user sees the same values.
  useEffect(() => {
    let cancelled = false;
    fetchRemote().then((remote) => {
      if (cancelled || !remote) return;
      const remoteStr = JSON.stringify(remote);
      const localStr  = JSON.stringify(loadFromStorage());
      if (remoteStr !== localStr) {
        try { localStorage.setItem(STORAGE_KEY, remoteStr); } catch {}
        setConfig(remote);
        window.dispatchEvent(new Event(CHANGE_EVENT));
      }
    });
    return () => { cancelled = true; };
  }, []);

  // React to same-tab and cross-tab changes.
  useEffect(() => {
    const onChange = () => setConfig(loadFromStorage());
    window.addEventListener(CHANGE_EVENT, onChange);
    const onStorage = (e) => { if (e.key === STORAGE_KEY) onChange(); };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  function saveLocal(next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
    setConfig(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }

  function updateTier(idx, patch) {
    const next = config.slice();
    next[idx] = { ...next[idx], ...patch };
    saveLocal(next);
  }

  function resetDefaults() {
    saveLocal(DEFAULTS);
  }

  /**
   * Push the current config to the shared server file. No auth required —
   * the admin panel is gated by wallet connection on the frontend.
   * Returns { ok: true } on success or throws with a human-readable message.
   */
  async function publishToServer(explicitConfig) {
    const payload = explicitConfig || config;
    const r = await fetch(REMOTE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      let msg = `Publish failed (HTTP ${r.status}).`;
      try { const j = await r.json(); if (j?.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    return { ok: true };
  }

  return { config, updateTier, resetDefaults, publishToServer };
}

// Convenience: format a tier's display range as "1.00 to 2.00 XT"
export function formatXtRange(tierIdx, config) {
  const c = config?.[tierIdx];
  if (!c) return '—';
  const fmt = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };
  return `${fmt(c.minXt)} to ${fmt(c.maxXt)}`;
}

/**
 * USDT equivalent of a tier's XT display range at the current on-chain XT price.
 * Returns "A to B" (no unit suffix) so callers can format the label themselves.
 * `xtPriceBn` is a BigNumber of USDT per 1 XT (18 decimals) — same shape as
 * what useXtPrice() yields.
 */
export function formatUsdtRangeFromXt(tierIdx, config, xtPriceBn) {
  const c = config?.[tierIdx];
  if (!c) return '—';
  let priceNum = 1;
  try {
    // xtPriceBn is a BigNumber (18 decimals). Convert to plain float for display math.
    if (xtPriceBn && typeof xtPriceBn.toString === 'function') {
      const s = xtPriceBn.toString();
      // shift decimals: divide by 1e18
      priceNum = Number(s) / 1e18;
    } else if (typeof xtPriceBn === 'number') {
      priceNum = xtPriceBn;
    }
  } catch {
    priceNum = 1;
  }
  if (!isFinite(priceNum) || priceNum <= 0) priceNum = 1;

  const minU = Number(c.minXt) * priceNum;
  const maxU = Number(c.maxXt) * priceNum;
  const fmt = (n) => {
    if (!isFinite(n)) return '—';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  return `${fmt(minU)} to ${fmt(maxU)}`;
}
